import gsap from 'gsap';
import serialize from 'form-serialize';
import $ from '../core/Dom';
import { post } from '../lib/ajax';
import Turnstile from '../lib/turnstile';

const INTERACTION_EVENTS = ['focusin', 'pointerdown'];

export default form => {

    const $form = $(form);
    const errorMessage = $form.find('[data-error]').get(0);
    const receipt = $form.find('[data-receipt]').get(0);
    const submitBtn = $form.find('button[type="submit"]').get(0);
    const turnstileContainer = $form.find('[data-turnstile]').get(0);
    const turnstile = turnstileContainer ? Turnstile(turnstileContainer, { onChange: () => onTurnstileChange() }) : null;

    let isSubmitting = false;

    const disableSubmit = () => {
        submitBtn.setAttribute('aria-disabled', 'true');
    };

    const enableSubmit = () => {
        // With Turnstile, the submit button stays disabled until the widget has a token
        if (turnstile && !turnstile.hasToken()) {
            return;
        }
        submitBtn.removeAttribute('aria-disabled');
    };

    const onTurnstileChange = () => {
        if (isSubmitting) {
            return;
        }
        if (turnstile.hasToken()) {
            enableSubmit();
        } else {
            disableSubmit();
        }
    };

    const reset = () => {
        // Clear input fields
        $form.find('input:not([type="hidden"]),textarea').get().forEach(input => {
            if (['radio', 'checkbox'].indexOf(input.getAttribute('type')) > -1) {
                input.checked = false;
            } else {
                input.value = null;
            }
        });
        // Hide receipt and show submit button
        gsap.timeline({
            onComplete() {
                enableSubmit();
                receipt.hidden = true;
                const { activeElement } = document;
                if (!activeElement || activeElement === receipt || activeElement.offsetParent === null) {
                    submitBtn.focus();
                }
            }
        })
            .to(receipt, { opacity: 0, scale: 0.9, duration: 0.3 }, 0)
            .to(submitBtn, { opacity: 1, scale: 1, duration: 0.3 }, 0.15);
        receipt.removeEventListener('blur', reset);
    };

    const showReceipt = () => {
        receipt.hidden = false;
        receipt.focus();
        gsap.timeline()
            .to(submitBtn, { opacity: 0, scale: 0.9, duration: 0.3 }, 0)
            .fromTo(receipt, { opacity: 0, scale: 0.9 }, { opacity: 1, scale: 1, duration: 0.3 }, 0.15);
        receipt.addEventListener('blur', reset);
    };

    const showGenericError = () => {
        if (!errorMessage) {
            return;
        }
        errorMessage.hidden = false;
        errorMessage.focus();
    };

    const clearErrors = () => {
        if (errorMessage) {
            errorMessage.hidden = true;
        }
        $form.find('[data-errors]').each(node => {
            node.textContent = '';
            node.hidden = true;
        });
        $form.find('[data-input].error').removeClass('error');
    };

    const getErrorNames = (errors, prefix = '') => Object.keys(errors || {}).reduce((carry, key) => {
        if (typeof errors[key] === 'object' && !Array.isArray(errors[key])) {
            return {
                ...carry,
                ...getErrorNames(errors[key], key)
            };
        }
        const name = prefix ? `${prefix}[${key}]` : key;
        return {
            ...carry,
            [name]: errors[key]
        };
    }, {});

    const showErrors = (errors = []) => {

        clearErrors();

        const errorsByName = getErrorNames(errors);
        const errorNames = Object.keys(errorsByName);

        if (!errorNames.length) {
            showGenericError();
            return;
        }

        errorNames.forEach(name => {
            const input = $form.find(`[data-input="${name}"]`).get(0);
            if (input) {
                input.classList.add('error');
            }
            const errorsDiv = $form.find(`[data-errors="${name}"]`).get(0);
            if (errorsDiv) {
                errorsDiv.hidden = false;
                $(errorsDiv).html([].concat(errorsByName[name]).join('<br/>'));
            }
        });

        const firstErrorInput = $form.find('[data-input].error').find('input:not([type="hidden"]),textarea,select').get(0);
        if (firstErrorInput) {
            firstErrorInput.focus();
        }

        enableSubmit();

    };

    const onSubmit = e => {

        e.preventDefault();

        if (isSubmitting || submitBtn.hasAttribute('aria-disabled')) {
            return;
        }

        clearErrors();

        isSubmitting = true;
        disableSubmit();

        form.classList.add('submitting');

        const url = window.location.href.split('?')[0].split('#')[0];

        // Wait for a Turnstile token first – the widget puts it in a hidden input, so it gets serialized with the rest
        (turnstile ? turnstile.getToken() : Promise.resolve())
            .then(() => {
                // Same fields as before (form-serialize skips empty ones), as FormData so ajax.js posts it as-is
                const data = new FormData();
                new URLSearchParams(serialize($form.get(0))).forEach((value, key) => data.append(key, value));
                return post(url, { body: data });
            })
            .then(res => {
                if (res.status !== 200) {
                    throw new Error();
                }
                return res.json();
            })
            .then(body => {
                const { success, errors = [] } = body || {};
                clearErrors();
                if (success) {
                    showReceipt();
                } else {
                    showErrors(errors);
                }
            })
            .catch(error => {
                console.error(error);
                showErrors();
            })
            .then(() => {
                isSubmitting = false;
                form.classList.remove('submitting');
                // The token may have been spent, so always get a fresh one for the next submission
                // (this keeps the submit button disabled until the new token is in)
                if (turnstile) {
                    turnstile.reset();
                }
                enableSubmit();
            });
    };

    const onFirstInteraction = () => {
        INTERACTION_EVENTS.forEach(type => form.removeEventListener(type, onFirstInteraction));
        turnstile.render()
            .catch(error => {
                // The submit button can't be enabled without Turnstile, so say something
                console.error(error);
                showGenericError();
            });
    };

    const init = () => {
        $form.on('submit', onSubmit);
        if (turnstile) {
            INTERACTION_EVENTS.forEach(type => form.addEventListener(type, onFirstInteraction));
        }
        $form.find('a[href]:not([href^="#"])').each(link => {
            $(link).attr({
                target: '_blank',
                rel: 'noopener noreferrer'
            });
        });
    };

    const destroy = () => {
        $form.off('submit', onSubmit);
        if (turnstile) {
            INTERACTION_EVENTS.forEach(type => form.removeEventListener(type, onFirstInteraction));
            turnstile.destroy();
        }
    };

    return {
        init,
        destroy
    };
};
